/**
 * Area Row
 *
 * One product area — drag handle, inline-editable title + inline
 * one-line description, a coverage bar with per-segment tooltips plus two
 * icon+percentage stat chips ("% passing" of what's mapped, "% covered" of
 * the whole area), "＋ capability", and a collapse toggle (via
 * `CollapsibleSection` in `caret` mode, since the header itself contains its
 * own inputs and buttons) — followed by its own capability rows. Also
 * renders the "Unassigned" pseudo-group when `area` is null: no drag handle,
 * no editing, no coverage bar (it's not a real area), but the same
 * droppable/sortable body so capabilities can be dragged in and out of it
 * like any other group.
 *
 * @module features/product-map/components/AreaRow
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChartPieSlice, CheckCircle, DotsSixVertical, Plus, Trash, Warning } from '@phosphor-icons/react';
import {
  Button,
  Badge,
  CollapsibleSection,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  EditableBadge,
  Flex,
  InlineEditableText,
  MultiSegmentBar,
  Text,
  Tooltip,
  TooltipTrigger,
  chartThemes,
  cn,
  useTheme,
  type BarSegment,
} from '@chatsdet/ui';
import {
  areaContainerId,
  COVERAGE_STATUS_LABEL,
  coverageStatusColor,
  deriveAreaCoverageBarSegments,
  deriveAreaCoverageStats,
  rollUpAreaCoverage,
} from '../productMapLogic';
import type { ProductMapStatus } from '@chatsdet/types';
import { STAGE_OPTIONS, STAGE_VARIANT } from '../stage';
import { useDeprecateArea, useUpdateArea } from '../useProductMap';
import type { ProductArea, ProductCapability } from '../types';
import { CapabilityRow } from './CapabilityRow';

/** Tooltip headline + second line for a stat chip — one object feeds both
 * the tooltip and the trigger's `aria-label`, so the two can't drift apart. */
type ChipCopy = { headline: string; detail: string };

/**
 * One "icon + percentage" chip in the header — used for both the passing and
 * covered stats. Local to this file: it's a thin, single-purpose pairing of
 * an icon with a tooltip-backed percentage, not a shape any other screen
 * needs yet.
 */
function StatChip({ icon, pct, copy }: { icon: ReactNode; pct: number | null; copy: ChipCopy }) {
  return (
    <TooltipTrigger delay={150}>
      {/* Fixed width (fits up to "100%") so the "+"/trash buttons don't
          jitter horizontally as the percentage's digit count changes
          between area rows. `tabIndex={0}` makes the tooltip's second line
          reachable by keyboard, not just mouse hover. */}
      <span
        tabIndex={0}
        className="inline-flex w-14 items-center justify-end gap-1 tabular-nums text-xs text-muted-foreground"
        role="img"
        aria-label={copy.headline}
      >
        {icon}
        {pct === null ? '—' : `${pct}%`}
      </span>
      <Tooltip variant="light">
        <div className="font-semibold">{copy.headline}</div>
        <div className="text-muted-foreground">{copy.detail}</div>
      </Tooltip>
    </TooltipTrigger>
  );
}

type Props = {
  /** null renders the "Unassigned" pseudo-group. */
  area: ProductArea | null;
  capabilities: ProductCapability[];
  onOpenCapability: (id: string) => void;
  onAddCapability: (areaId: string | null) => void;
  /** The capability id just created via `onAddCapability`, so its row opens already editable. */
  autoEditCapabilityId?: string | null;
  /** True when this area was just created via "＋ area" — its title opens already editable. */
  autoEditTitle?: boolean;
  /** Subtle background highlight for a deep-linked `?area=` target. */
  highlighted?: boolean;
};

export function AreaRow({
  area,
  capabilities,
  onOpenCapability,
  onAddCapability,
  autoEditCapabilityId,
  autoEditTitle = false,
  highlighted = false,
}: Props) {
  const { config } = useTheme();
  const updateArea = useUpdateArea();
  // `area` is null for the "Unassigned" pseudo-group, which never renders the
  // deprecate button/dialog below (see the `!area` branch in `actions`) — so
  // `deprecateArea.mutate` can never actually fire with this fallback empty
  // propertyId. Kept unconditional (rather than skipped) only because hooks
  // can't be called conditionally; the `''` never reaches `onMutate`.
  const deprecateArea = useDeprecateArea(area?.propertyId ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);

  const containerId = areaContainerId(area?.id ?? null);
  // Discriminates this droppable from the capability sortables and area
  // sortables competing for the same collision-detection pass — see
  // `productMapCollision.ts`.
  const { setNodeRef: setDroppableRef } = useDroppable({ id: containerId, data: { type: 'container' } });
  const sortable = useSortable({
    // The Unassigned pseudo-group (`area === null`) has no area sortable of
    // its own, but `useSortable` can't be called conditionally — it still
    // needs SOME id. Using `containerId` here used to collide with the
    // `useDroppable({ id: containerId })` above (two droppables sharing one
    // id); it was masked only because `disabled: true` also disables this
    // sortable's droppable half. Suffixing keeps the id space distinct even
    // though this sortable is always disabled for the Unassigned case.
    id: area ? area.id : `${containerId}:disabled-sortable`,
    data: { type: 'area', label: area?.title },
    disabled: !area,
  });

  const capabilityIds = capabilities.map((c) => c.id);
  const rollup = rollUpAreaCoverage(capabilities);
  const stats = deriveAreaCoverageStats(rollup);
  const palette = chartThemes[config.theme];

  // Bar-segment colors come from the shared `coverageStatusColor` mapping so
  // they can't fall out of sync with `deriveAreaCoverageBarSegments`'s
  // bar-order array below. `CapabilityRow`'s row dot deliberately reads from
  // this same mapping (not Tailwind classes) so the dot and the bar always
  // agree.
  const segments: BarSegment[] = deriveAreaCoverageBarSegments(rollup, stats).map((segment) => ({
    label: COVERAGE_STATUS_LABEL[segment.status],
    value: segment.value,
    color: coverageStatusColor(palette, segment.status),
    tooltip: (
      <>
        <div className="font-semibold">{segment.headline}</div>
        {segment.detail && <div className="text-neutral-500">{segment.detail}</div>}
      </>
    ),
  }));

  // The two header stat chips' copy, derived once each so the chip's tooltip
  // and its `aria-label` (built by `StatChip` from the same object) can never
  // drift apart.
  const passingCopy: ChipCopy =
    stats.passingPct === null
      ? {
          headline: rollup.total === 0 ? 'No capabilities in this area yet' : 'No capabilities mapped yet',
          detail:
            rollup.total === 0
              ? 'Add a capability to get started.'
              : 'Link a flow to start tracking pass rate.',
        }
      : {
          headline: `${stats.passingPct}% of mapped capabilities passing`,
          detail: `${rollup.covered} of ${stats.mapped} mapped`,
        };

  const mappedCopy: ChipCopy =
    stats.mappedPct === null
      ? { headline: 'No capabilities in this area yet', detail: 'Add a capability to get started.' }
      : {
          headline: `${stats.mappedPct}% of capabilities covered`,
          detail: `${stats.mapped} of ${rollup.total} mapped to a flow`,
        };

  const stop = (e: SyntheticEvent) => e.stopPropagation();

  const style = area
    ? {
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : 1,
      }
    : undefined;

  const header = (
    // `items-center` (not `items-start`) so the grip lines up against the
    // vertical center of the two-line title+description stack, matching
    // `CollapsibleSection`'s own caret — which centers itself against this
    // same row from outside (see its `items-center` trigger row) — rather
    // than introducing a second, inconsistent alignment rule next to it.
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {area ? (
        <button
          type="button"
          className="shrink-0 -ml-1 flex items-center px-1 text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
          aria-label={`Reorder area: ${area.title}`}
          onClick={stop}
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}

      {area ? (
        // Title over description, each on its own line and free to use the
        // row's full remaining width (no more `max-w-[40%]` — that cap only
        // existed to referee the two fighting for one line). `min-w-0` here
        // (a flex-ROW item) is what lets the stack shrink below its
        // children's natural content width in the first place; `gap-0.5` is
        // breathing room between the two lines that still reads tight next
        // to the `py-1.5` capability rows below. Each line keeps its own
        // `truncate` — still a dense list, not a card, so long values
        // ellipsis rather than wrap.
        <div className="flex min-w-0 flex-1 flex-col gap-0.5" onClick={stop} role="presentation">
          {/* This row already spans the column above (cross-axis stretch);
              a `flex-1` here would only grow height, not width. The growing
              that matters happens one level in — the title's own wrapper
              takes `flex-1` while editing; the edit-mode root it contains
              has no width class of its own, so it fills whatever width that
              wrapper gives it, and only then does the field's `100%`
              resolve against the row's remaining width instead of a
              shrink-wrapped box. */}
          <div className="flex min-w-0 items-baseline gap-1.5">
            {/* `mr-2` clears the edit-mode field's left bleed (see the
                bleed note in InlineEditableText.tsx), same as the
                capability row's code chip. */}
            <Text size="xs" className="mr-2 shrink-0 font-mono text-muted-foreground/60">
              {area.code}
            </Text>
            <div className={cn('min-w-0', editingTitle && 'flex-1')}>
              <InlineEditableText
                value={area.title}
                onSave={(title) => updateArea.mutate({ id: area.id, title })}
                onEditingChange={setEditingTitle}
                ariaLabel={`Area title: ${area.title}`}
                placeholder="Untitled area"
                className="min-w-0 truncate text-[15px] font-semibold"
                autoEdit={autoEditTitle}
              />
            </div>
            {area.kind === 'agent' && (
              <Badge size="sm" variant="secondary" className="shrink-0">
                Agent
              </Badge>
            )}
          </div>
          <InlineEditableText
            value={area.description ?? ''}
            onSave={(description) => updateArea.mutate({ id: area.id, description })}
            ariaLabel={`Area description: ${area.title}`}
            placeholder="Add a one-line description…"
            // Optional field: clearing it back to empty must persist.
            allowEmpty
            className="truncate text-xs text-muted-foreground"
          />
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1" role="presentation">
            <Text size="sm" weight="semibold" className="text-muted-foreground">
              Unassigned
            </Text>
          </div>
          <Text size="xs" className="shrink-0 tabular-nums text-muted-foreground">
            {capabilities.length}
          </Text>
        </>
      )}
    </div>
  );

  const actions = (
    <div className="flex items-center gap-2" onClick={stop} role="presentation">
      {area && (
        <>
          <EditableBadge<ProductMapStatus>
            value={area.status}
            options={STAGE_OPTIONS}
            onChange={(status) => updateArea.mutate({ id: area.id, status })}
            variant={STAGE_VARIANT[area.status]}
            size="sm"
            aria-label={`Stage for area ${area.title}`}
          />
          <MultiSegmentBar
            segments={segments}
            total={rollup.total}
            size="sm"
            className="w-24"
            aria-label={`Coverage for ${area.title}`}
          />
          <StatChip
            icon={<CheckCircle size={13} weight="fill" style={{ color: palette.success }} />}
            pct={stats.passingPct}
            copy={passingCopy}
          />
          <StatChip
            icon={<ChartPieSlice size={13} className="text-muted-foreground" />}
            pct={stats.mappedPct}
            copy={mappedCopy}
          />
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        tooltip={`Add capability to ${area?.title ?? 'Unassigned'}`}
        aria-label={`Add capability to ${area?.title ?? 'Unassigned'}`}
        onPress={() => onAddCapability(area?.id ?? null)}
      >
        <Plus size={14} weight="bold" />
      </Button>

      {area && (
        <>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            tooltip="Deprecate area"
            aria-label={`Deprecate area: ${area.title}`}
            // Hidden until the row is hovered/focused — this is the most
            // impactful action on the page (it takes every capability filed
            // under the area with it), so it shouldn't be one of the first
            // things a glance at the row shows.
            className="text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100"
            onPress={() => setConfirmDelete(true)}
          >
            <Trash size={14} />
          </Button>

          <DialogTrigger isOpen={confirmDelete} onOpenChange={setConfirmDelete} size="md">
            <span />
            <Dialog title={`Deprecate "${area.title}"?`}>
              <DialogContent>
                <Flex align="start" gap="md" className="py-2">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                    <Warning size={22} weight="fill" className="text-destructive" />
                  </span>
                  <Text size="sm" className="leading-relaxed text-muted-foreground">
                    {capabilities.length > 0 ? (
                      <>
                        <Text as="span" weight="semibold" className="text-foreground">
                          {area.title}
                        </Text>{' '}
                        can only be deprecated after its{' '}
                        <Text as="span" weight="semibold" className="text-foreground">
                          {capabilities.length} capabilit{capabilities.length === 1 ? 'y' : 'ies'}
                        </Text>{' '}
                        {capabilities.length === 1 ? 'is' : 'are'} deprecated. The server will refuse
                        otherwise and name the blocking capabilities.
                      </>
                    ) : (
                      <>
                        This removes{' '}
                        <Text as="span" weight="semibold" className="text-foreground">
                          {area.title}
                        </Text>{' '}
                        from the active product map. It keeps its code, so a ticket that cites it
                        still resolves — you just won&rsquo;t see it here anymore.
                      </>
                    )}
                  </Text>
                </Flex>
              </DialogContent>
              <DialogFooter className="mt-6">
                <Button variant="secondary" onPress={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isDisabled={capabilities.length > 0}
                  onPress={() => {
                    deprecateArea.mutate(area.id);
                    setConfirmDelete(false);
                  }}
                >
                  Deprecate area
                </Button>
              </DialogFooter>
            </Dialog>
          </DialogTrigger>
        </>
      )}

      {!area && (
        /* Geometry-exact spacer: the icon Button's width comes from its
           padding, not a rem-based utility, so no `w-*` class can reliably
           match it (this app's 14px root makes `w-8` render 28px against the
           Button's 34px). Rendering the SAME trailing markup as a real area's
           actions — the trash Button plus the `DialogTrigger`'s placeholder
           `<span>` — under `invisible`, laid out with the same `gap-2` this
           wrapper mirrors internally, keeps the "＋" column aligned with the
           area rows by construction (a lone invisible Button undershoots by
           the gap+span an area row's `DialogTrigger` adds after its trash
           button). `invisible` also removes it from the tab order and the
           accessibility tree — and unlike a disabled trash, it doesn't imply
           Unassigned could be deleted. */
        <div className="invisible flex items-center gap-2" aria-hidden>
          <Button variant="ghost" size="sm" iconOnly aria-label="">
            <Trash size={14} />
          </Button>
          <span />
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={area ? sortable.setNodeRef : undefined}
      style={style}
      className={highlighted ? 'bg-primary/5 transition-colors' : undefined}
    >
      <CollapsibleSection
        triggerMode="caret"
        defaultExpanded
        hideBorder
        bodyClassName="pb-0"
        // `mx-0` cancels CollapsibleSection's default `-mx-1` bleed, which
        // would otherwise offset every area header 4px from the capability
        // rows beneath it inside this flush, bordered container. `group/row`
        // scopes the delete button's hover reveal in `actions` to this row.
        triggerClassName="mx-0 px-3 group/row"
        label={area?.title ?? 'Unassigned'}
        header={header}
        actions={actions}
      >
        {/* `pl-6` indents the capability rows under their area title. Without
            it the nesting reads flat: a capability's drag grip would land at
            the same x as the area's collapse caret. */}
        <div ref={setDroppableRef} className="divide-y divide-border/40 pl-6">
          <SortableContext items={capabilityIds} strategy={verticalListSortingStrategy}>
            {capabilities.length === 0 ? (
              <Text size="xs" className="block px-3 py-1.5 italic text-muted-foreground">
                No capabilities yet — drag one here, or add one.
              </Text>
            ) : (
              capabilities.map((capability) => (
                <CapabilityRow
                  key={capability.id}
                  capability={capability}
                  onOpen={() => onOpenCapability(capability.id)}
                  autoEditTitle={capability.id === autoEditCapabilityId}
                />
              ))
            )}
          </SortableContext>
        </div>
      </CollapsibleSection>
    </div>
  );
}
